import {getApp, initializeApp} from "firebase/app";
import {connectAuthEmulator, getAuth, indexedDBLocalPersistence, initializeAuth} from "firebase/auth";
import {getDatabase, ref} from "firebase/database";
import {getFirestore} from "firebase/firestore";
import {getAnalytics, logEvent, setUserProperties} from "firebase/analytics";
import {connectFunctionsEmulator, getFunctions, httpsCallable} from "firebase/functions";
import {getRemoteConfig} from "firebase/remote-config";
import {firebaseConfig} from "configs/firebaseConfig";
import {CustomProvider, initializeAppCheck, ReCaptchaEnterpriseProvider} from "firebase/app-check";
import {Capacitor, registerPlugin} from "@capacitor/core";
import {FirebaseAppCheck} from "@capacitor-firebase/app-check";
import {getStorage} from "firebase/storage";
import {FirebaseFunctions} from "@capacitor-firebase/functions";
import {FirebaseCrashlytics} from "@capacitor-firebase/crashlytics";
import {FirebasePerformance} from "@capacitor-firebase/performance";
import {FirebaseAnalytics} from "@capacitor-firebase/analytics";
import {FirebaseStorage} from "@capacitor-firebase/storage";
import {DataSource} from "typeorm";
import {CapacitorSQLite, SQLiteConnection} from "@capacitor-community/sqlite";
import {InitialMigration1737711384843} from "migrations/1737711384843-InitialMigration";
import {AddEntitlementField1737750000000} from "migrations/1737750000000-AddEntitlementField";
import {AddSubscriptionColumn1737711384844} from "migrations/1737711384844-AddSubscriptionColumn";
import {AddStatsMaxspeedColumn1738000000000} from "migrations/1738000000000-AddStatsMaxspeedColumn";
import {AddStravaUploadColumn1787270400000} from "migrations/1787270400000-AddStravaUploadColumn";
import {Point} from "entities/Point";
import {User} from "entities/User";
import {Navigation} from "entities/Navigation";
import {ClevertapPlugin} from "services/ClevertapPlugin";

export const AnalyticsTrackerPlugin = registerPlugin<ClevertapPlugin>("AnalyticsTrackerPlugin", {
  web: () => new ClevertapPlugin()
});

export const isAndroid = Capacitor.isNativePlatform();

const app = initializeApp(firebaseConfig);

const getFirebaseAuth = () => {
  if (isAndroid) {
    return initializeAuth(getApp(), {
      persistence: indexedDBLocalPersistence
    });
  } else {
    return getAuth(app);
  }
};

export const auth = await getFirebaseAuth();

export const dbRef = ref(getDatabase(app));

export const firebaseAnalytics = getAnalytics(app);
setUserProperties(firebaseAnalytics, {"firebase_event_origin[session_start]": "web"});
let _logEvent = (name, data) => {
  logEvent(firebaseAnalytics, name, data);
  return Promise.resolve();
};
let _setScreenView = (name) => {
  logEvent(firebaseAnalytics, "screen_view", {
    firebase_screen: name,
    firebase_screen_class: name
  });
  return Promise.resolve();
};

export const firestore = getFirestore(app);

export const functions = getFunctions(app);
let _httpsCallable: any = httpsCallable;

// Initialize Remote Config
export const remoteConfig = getRemoteConfig(app);

// Initialize storage - use Capacitor Firebase Storage for native platforms
export const storage = isAndroid ? FirebaseStorage : getStorage(app);

if (process.env.NODE_ENV === "development") {
  // Opt-in, with `npm run serve-functions_and_DB_local_auth`: accounts then live in
  // the Auth emulator only, so tests never create real ones.
  if (!isAndroid && process.env.REACT_APP_AUTH_EMULATOR === "true") {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", {disableWarnings: true});
  }
  const enableFunctionsEmulator = process.env.REACT_APP_ENABLE_FUNCTIONS_EMULATOR === "true";
  if (!isAndroid && enableFunctionsEmulator) {
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    connectFunctionsEmulator(functions, "localhost", 5001);
    setUserProperties(firebaseAnalytics, {
      "firebase_event_origin[session_start]": "web",
      dev_mode: "true"
    });
  }

  if (isAndroid && enableFunctionsEmulator) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    await FirebaseFunctions.useEmulator({
      host: location.hostname, // Computer host
      port: 5001
    });
  }
}

if (isAndroid) {
  const enableAppCheck = true;
  if (enableAppCheck) {
    console.log(
      `Initializing Firebase App Check for native platform in ${process.env.NODE_ENV === "development" ? "debug" : "prod"} mode`
    );

    // 1. Initialize on the native layer
    await FirebaseAppCheck.initialize({
      debug: process.env.NODE_ENV === "development",
      isTokenAutoRefreshEnabled: true
    });

    // 2. Set up a custom provider and Initialize on the web layer
    const customProvider = new CustomProvider({
      getToken: () => {
        return FirebaseAppCheck.getToken({forceRefresh: true}) as any;
      }
    });
    await initializeAppCheck(app, {provider: customProvider, isTokenAutoRefreshEnabled: true});
  }

  _httpsCallable = (_, name, _opts) => {
    return async (data) => {
      return await FirebaseFunctions.callByName({
        name,
        data
      });
    };
  };

  await FirebasePerformance.setEnabled({enabled: true});
  await FirebaseCrashlytics.setEnabled({enabled: true});
  await FirebaseAnalytics.setEnabled({enabled: true});
  await FirebaseCrashlytics.sendUnsentReports();

  await FirebaseAnalytics.setUserProperty({
    key: "session_start",
    value: "android"
  });
  _logEvent = async (name, data) => {
    await FirebaseAnalytics.logEvent({name, data} as any);
    AnalyticsTrackerPlugin.trackEvent({
      eventName: name,
      properties: data
    });
  };
  _setScreenView = async (name) => {
    await FirebaseAnalytics.setCurrentScreen({screenName: name});
  };
} else {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(process.env.REACT_APP_RECAPTCHA),
    isTokenAutoRefreshEnabled: true
  });
}

export const httpsCallableCustom = _httpsCallable;
export const customLogEvent = _logEvent;
export const customSetScreenView = _setScreenView;

const sqliteConnection = new SQLiteConnection(CapacitorSQLite);

const localDBStorage = new DataSource({
  type: "capacitor", // This uses the SQLite database
  database: "waterspeed_db",
  driver: sqliteConnection,
  entities: [Point, User, Navigation],
  logging: ["error", "migration"],
  migrations: [
    InitialMigration1737711384843,
    AddEntitlementField1737750000000,
    AddSubscriptionColumn1737711384844,
    AddStatsMaxspeedColumn1738000000000,
    AddStravaUploadColumn1787270400000
  ],
  synchronize: false, // !!!You will lose all data in database if set to `true`
  migrationsRun: false, // Required with capacitor type
  enableWAL: true
} as any);
let _localDBStorage: any = null;
// Concurrent callers (e.g. CompareActivitiesPage loading two activities via Promise.all) must
// await the same in-flight init instead of getting `null` back — a boolean "busy" flag can't tell
// a second caller to wait, so it used to bail out to LocalDbService's polling fallback, which
// could spin indefinitely instead of ever seeing the result of the init this call kicked off.
let _localDBInitPromise: Promise<any> | null = null;

export const getLocalDB = async (index?: any) => {
  if (!isAndroid) {
    return null;
  }

  if (_localDBStorage !== null) {
    return _localDBStorage;
  }

  if (_localDBInitPromise) {
    return _localDBInitPromise;
  }

  _localDBInitPromise = initializeLocalDB();
  try {
    return await _localDBInitPromise;
  } finally {
    _localDBInitPromise = null;
  }
};

const initializeLocalDB = async () => {
  await sqliteConnection.checkConnectionsConsistency().catch((e) => {
    console.log("checkConnectionsConsistency error ", e);
    return {};
  });

  console.log("Initializing local DB ...");

  await localDBStorage.initialize();
  console.log("Local DB initialized");

  if (localDBStorage.isInitialized) {
    console.log("Running local DB migrations ...");
    // run the migrations
    await localDBStorage.runMigrations();
    console.log("Migrations completed");

    // Ensure entitlement column exists
    try {
      console.log("Checking for entitlement column...");
      await localDBStorage
        .query(
          `
        SELECT sql FROM sqlite_master WHERE type='table' AND name='user'
      `
        )
        .then(async (result) => {
          if (result && result.length > 0) {
            const createTableSQL = result[0].sql;
            if (!createTableSQL.includes("entitlement")) {
              console.log("Entitlement column not found, adding it...");
              await localDBStorage.query(`ALTER TABLE "user" ADD COLUMN "entitlement" text`);
              console.log("Entitlement column added successfully");
            } else {
              console.log("Entitlement column already exists");
            }
          }
        });
    } catch (e) {
      console.error("Error checking/adding entitlement column:", e);
    }
  }
  _localDBStorage = localDBStorage;

  return _localDBStorage;
};
